import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { CardContainer } from 'components';
import { Card } from 'components/Card';
import { H2, Text } from 'components/Typography';
import { Direction, lineHeight, theme, toRem } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { getUserPendingAndExpiredPaymentStatsRequest, User } from 'store/User';
import { IPayoutStateByStatus, PayoutStatus } from 'types';
import { centsToDollarsDisplay } from 'utils';

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  align-self: stretch;

  h3 {
    font-size: ${toRem(theme.font.size.lg)};
    font-style: normal;
    font-weight: ${theme.font.weight.semiBold};
    line-height: ${lineHeight(theme.font.size.lg)};
  }

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const StatusCard = styled(Card)`
  flex-flow: column nowrap;
  align-items: start;
  padding: ${toRem(theme.space.lg)};
  min-height: unset;
  gap: ${toRem(theme.space.md)};

  header {
    width: 100%;
    justify-content: space-between;
    text-transform: capitalize;
  }

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const Totals = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
`;

const HelpLink = styled.button`
  color: ${theme.color.primary[500]};
  font-size: ${toRem(theme.font.size.xs)};

  &:hover {
    color: ${theme.color.primary[600]};
  }

  &:active {
    color: ${theme.color.primary[500]};
  }
`;

interface IAccountHealthProps {
  siteId?: string;
  dataCy?: string;
  onHelpAction: (status: PayoutStatus) => void;
}

const buildStatusCard = (status: PayoutStatus, payouts: IPayoutStateByStatus, helpAction: (payoutStatus: PayoutStatus) => void, dataCy: string) => {
  if (!payouts) return <></>;

  return (
    <StatusCard data-cy={dataCy}>
      <Header data-cy={`${dataCy}-header`}>
        <Text size="xsmall" fontWeight="medium">
          {status} Payments
        </Text>
        <HelpLink onClick={() => helpAction(status)}>Get Help</HelpLink>
      </Header>
      <Totals data-cy={`${dataCy}-totals`}>
        <H2 fontWeight="semibold">{centsToDollarsDisplay(payouts.net)}</H2>
        <Text size="xxsmall">
          ({payouts.count} payment{payouts.count > 1 ? 's' : ''})
        </Text>
      </Totals>
    </StatusCard>
  );
};

export const AccountHealth = ({ siteId, onHelpAction, dataCy = 'account-health' }: IAccountHealthProps) => {
  const dispatch = useDispatch();
  const payoutStatsByStatus = useSelector((state: IStore) => state.user?.pendingAndExpiredPaymentStats?.byStatus || []);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_USER_PENDING_AND_EXPIRED_PAYMENT_STATS_REQUEST));

  useEffect(() => {
    dispatch(getUserPendingAndExpiredPaymentStatsRequest(siteId));
  }, [dispatch, siteId]);

  // Find pending and expired payouts (the stats will only return one record for each status)
  const pendingPayouts = payoutStatsByStatus.find((p) => p.status === PayoutStatus.PENDING);
  const expiredPayouts = payoutStatsByStatus.find((p) => p.status === PayoutStatus.EXPIRED);

  // Do not show when loading or no pending or expired payouts
  if (isLoading || (!pendingPayouts && !expiredPayouts)) return <></>;

  return (
    <CardContainer title="Account Health" iconType="Error" iconFill={theme.color.danger[50]} iconHaloColor={theme.color.danger[25]} direction={Direction.ROW} dataCy={dataCy}>
      {buildStatusCard(PayoutStatus.PENDING, pendingPayouts, onHelpAction, `${dataCy}-status-card-pending`)}
      {buildStatusCard(PayoutStatus.EXPIRED, expiredPayouts, onHelpAction, `${dataCy}-status-card-expired`)}
    </CardContainer>
  );
};
